import React, { useCallback } from 'react'
import {
  Alert,
  Form,
  Text,
  SignatureInputField,
  // @ts-ignore no types
} from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import _cx from 'classnames/bind'
// @ts-ignore no types
import cls from './styles.scss'
import { Context, FirstLookFlowData } from './firstLookFlow'

const cx = _cx.bind(cls)

interface EntityAgreementProps {
  isCaliforniaSpecialTreatmentRequired: boolean
}

const EntityAgreement = ({
  isCaliforniaSpecialTreatmentRequired,
}: EntityAgreementProps) => {
  return (
    <>
      {isCaliforniaSpecialTreatmentRequired ? (
        <>
          <br />
          <Text>
            The undersigned hereby represents and declares under the penalty of
            perjury that the prospective purchaser is one of the following:
          </Text>
          <ul className={cx('first-look-special-treatment-agreement-list')}>
            <br />
            <li>
              <Text>
                A nonprofit corporation that meets all of the following
                requirements:
              </Text>
              <ul className={cx('u-mt-1', 'u-mb-1')}>
                <li>
                  <Text>
                    The nonprofit corporation has a determination letter from
                    the Internal Revenue Service affirming its tax-exempt status
                    pursuant to Section 501(c)(3) of the Internal Revenue Code
                    and is not a private foundation as that term is defined in
                    Section 509 of the Internal Revenue Code.
                  </Text>
                </li>
                <li>
                  <Text>The nonprofit corporation is based in California.</Text>
                </li>
                <li>
                  <Text>
                    All of the board members of the nonprofit corporation have
                    their primary residence in California.
                  </Text>
                </li>
                <li>
                  <Text>
                    The primary activity of the nonprofit corporation is the
                    development and preservation of affordable rental or home
                    ownership housing in California.
                  </Text>
                </li>
              </ul>
            </li>
            <li>
              <Text>
                A community land trust based in California, as defined in clause
                (ii) of subparagraph (C) of paragraph (11) of subdivision (a) of
                Section 402.1 of the Revenue and Taxation Code.
              </Text>
            </li>
            <li>
              <Text>
                A limited-equity housing cooperative, as defined in Section 817,
                that is based in California.
              </Text>
            </li>
            <li>
              <Text>
                The state, the Regents of the University of California, a
                county, city, district, public authority, or public agency, and
                any other political subdivision or public corporation in the
                state.
              </Text>
            </li>
            <br />
          </ul>
        </>
      ) : (
        <>
          <Text>
            I am an employee or buyer’s agent representing an entity that is one
            of the following:
          </Text>
          <ol>
            <li>
              <Text>A local government entity</Text>
            </li>
            <li>
              <Text>An approved non-profit</Text>
            </li>
          </ol>
        </>
      )}
    </>
  )
}

interface OwnerOccupantAgreementProps {
  isCaliforniaSpecialTreatmentRequired: boolean
}

const OwnerOccupantAgreement = ({
  isCaliforniaSpecialTreatmentRequired,
}: OwnerOccupantAgreementProps) => {
  return (
    <>
      {isCaliforniaSpecialTreatmentRequired ? (
        <>
          <br />
          <Text>
            The undersigned hereby represents and declares under the penalty of
            perjury that they are a natural person that:
          </Text>
          <ul className={cx('first-look-special-treatment-agreement-list')}>
            <br />
            <li>
              <Text>
                Will occupy the property as their primary residence within 60
                days of the trustee’s deed being recorded.
              </Text>
            </li>
            <li>
              <Text>Will maintain their occupancy for at least one year.</Text>
            </li>
            <li>
              <Text>Is not any of the following:</Text>
            </li>
            <ul>
              <li>
                <Text>The mortgagor or trustor.</Text>
              </li>
              <li>
                <Text>
                  The child, spouse, or parent of the mortgagor or trustor.
                </Text>
              </li>
              <li>
                <Text>
                  The grantor of a living trust that was named in the title to
                  the property when the notice of default was recorded.
                </Text>
              </li>
              <li>
                <Text>
                  An employee, officer, or member of the mortgagor or trustor.
                </Text>
              </li>
              <li>
                <Text>
                  A person with an ownership interest in the mortgagor, unless
                  the mortgagor is a publicly traded company.
                </Text>
              </li>
            </ul>
            <li>
              <Text>
                Is not acting as the agent of any other person or entity in
                purchasing the real property.
              </Text>
            </li>
            <br />
          </ul>
        </>
      ) : (
        <>
          <Text>I am the buyer or their agent, and I confirm that:</Text>
          <ol>
            <li>
              <Text>
                I will occupy the property as my primary residence within 60
                days of closing.
              </Text>
            </li>
            <li>
              <Text>
                I will not sell or rent the property within one year of
                purchase.
              </Text>
            </li>
          </ol>
        </>
      )}
    </>
  )
}

type AcknowledgementsAndAgreementStepProps = {
  context: Context
  data: FirstLookFlowData
  setData: (data: FirstLookFlowData) => void
}

const AcknowledgementsAndAgreementStep = ({
  context,
  data,
  setData,
}: AcknowledgementsAndAgreementStepProps) => {
  const { isCaliforniaSpecialTreatmentRequired, isMobile } = context
  const onNameInputChange = useCallback(
    (e: any) => {
      setData({ ...data, fullName: e?.target?.value })
    },
    [data, setData]
  )

  return (
    <Form>
      <Text
        size={!isMobile ? 'big' : 'huge'}
        className={cx('title-center')}
        weight='heavy'
      >
        {isCaliforniaSpecialTreatmentRequired
          ? 'Acknowledgement and Affidavit'
          : 'Acknowledgements'}
      </Text>
      {isCaliforniaSpecialTreatmentRequired && (
        <Text weight='heavy' className={cx('u-mt-2')} size='large'>
          You must read and accept the entire agreement in order to proceed
        </Text>
      )}
      {data.selection ===
      graph.enums.FirstLookBuyerSelectionType.EntityApproved ? (
        <EntityAgreement
          isCaliforniaSpecialTreatmentRequired={
            !!isCaliforniaSpecialTreatmentRequired
          }
        />
      ) : (
        <OwnerOccupantAgreement
          isCaliforniaSpecialTreatmentRequired={
            !!isCaliforniaSpecialTreatmentRequired
          }
        />
      )}
      {!isCaliforniaSpecialTreatmentRequired && (
        <Text>
          By typing your name, you acknowledge that the buyer meets the criteria
          above. The buyer will be required to certify this information during
          the contracting process.
        </Text>
      )}
      <div className={cx('u-mb-3', 'u-mt-3')}>
        <Alert
          header='Please Note'
          content={
            !isCaliforniaSpecialTreatmentRequired ? (
              'Any misrepresentations may result in suspension of your account and/or monetary penalties.'
            ) : (
              <>
                <span>
                  By typing your name below, you certify under penalty of
                  perjury under the laws of the State of California that the
                  prospective purchaser meets the criteria above.
                </span>
                <span>
                  Further, any misrepresentation may result in suspension of
                  your account or imposition of damages in accordance with
                  Auction.com's Participation Terms.
                </span>
              </>
            )
          }
          role='caution'
          showIcon
        />
      </div>

      <SignatureInputField
        // @ts-ignore fix types
        name='fullname'
        label='Type Your Full Name'
        subLabel={
          isCaliforniaSpecialTreatmentRequired
            ? 'By typing your name, you are electronically signing the above affidavit and agree that your electronic signature will be enforceable as an original to the same extent as a handwritten signature.'
            : 'To confirm you have read and acknowledge the above conditions'
        }
        elmId='full_name'
        onChange={onNameInputChange}
        value={data.fullName}
        maxLength={80}
      />
    </Form>
  )
}
export default AcknowledgementsAndAgreementStep
